import { useEffect, useMemo, useState } from 'react';
import { useTenant } from '@/contexts/TenantContext';
import { loadTenantBoundaries } from '@/utils/boundaryCache';
import {
  boundsFromPolygons,
  centerOfBounds,
  geometryToPolygons,
  zoomForBounds,
  type BoundaryGeometry,
  type LatLngBounds,
} from '@/utils/boundaryGeometry';

/** Fallback view: all of South Africa, for a user with no FPA context. */
const NATIONAL_CENTER: [number, number] = [-29.0, 26.0];
const NATIONAL_ZOOM = 6;
/** Zoom for an FPA that has a reference point but no boundary geometry. */
const POINT_ZOOM = 11;

const STORAGE_KEY = 'fpa-map-home:v1';

type StoredBounds = Record<string, [number, number, number, number]>;

function readStore(): StoredBounds {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredBounds) : {};
  } catch {
    return {};
  }
}

function readCachedBounds(tenantId: number | null): LatLngBounds | null {
  if (tenantId == null) return null;
  const box = readStore()[String(tenantId)];
  if (!Array.isArray(box) || box.length !== 4 || box.some((n) => !Number.isFinite(n))) {
    return null;
  }
  const [south, west, north, east] = box;
  return [[south, west], [north, east]];
}

function sameBounds(a: LatLngBounds | null, b: LatLngBounds | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a[0][0] === b[0][0] && a[0][1] === b[0][1] && a[1][0] === b[1][0] && a[1][1] === b[1][1]
  );
}

function writeCachedBounds(tenantId: number, bounds: LatLngBounds): void {
  try {
    const [[south, west], [north, east]] = bounds;
    const store = readStore();
    store[String(tenantId)] = [south, west, north, east];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* private mode / quota — the map still fits, it just re-fits next visit */
  }
}

export interface FpaMapHome {
  /** Seed for `MapCanvas`'s `center`. */
  center: [number, number];
  /** Seed for `MapCanvas`'s `zoom`. */
  zoom: number;
  /** Feed straight to `MapCanvas`'s `fitBoundsTo`; `false` when there is
   *  nothing to fit (no FPA selected, or an FPA with no geometry). */
  fitBoundsTo: { polygons: Array<{ positions: [number, number][] }> } | false;
}

/**
 * Where a tenant-scoped map should open: framed on the active FPA rather than
 * on the whole country.
 *
 * The bounding box is remembered in localStorage per FPA, so on every visit
 * after the first the map's very first paint is already on the FPA — there is
 * no national view to pan away from. `fitBoundsTo` then corrects it to the
 * exact extent once the boundaries resolve.
 *
 * Resolution order: cached bbox → the FPA's boundaries → the legacy embedded
 * `tenant.boundary` → the FPA's reference lat/lng → the national view. The
 * legacy field matters because the FPA Hub's draw/edit flow writes only the
 * boundaries table while older FPAs may only have `tenant.boundary`, so
 * either one can be the sole source of geometry.
 *
 * The returned `fitBoundsTo` keeps a stable identity while the bbox is
 * unchanged, so `FitBoundsToData` fits once per mount and a later data
 * refetch (filters, season, polling) never yanks the user's view back.
 */
export function useFpaMapHome(tenantId: number | null): FpaMapHome {
  const { currentTenant } = useTenant();
  const [bounds, setBounds] = useState<LatLngBounds | null>(() => readCachedBounds(tenantId));

  const legacyBoundary = (currentTenant?.boundary ?? null) as BoundaryGeometry | null;

  useEffect(() => {
    // Every write goes through the equality check: re-seeding the same box
    // must not hand back a fresh array and re-trigger the fit.
    const apply = (next: LatLngBounds | null) =>
      setBounds((prev) => (sameBounds(prev, next) ? prev : next));

    apply(readCachedBounds(tenantId));
    if (tenantId == null) return;

    let cancelled = false;
    loadTenantBoundaries(tenantId).then((list) => {
      if (cancelled) return;
      const resolved =
        boundsFromPolygons(list.flatMap((b) => geometryToPolygons(b.geometry, b.id))) ??
        boundsFromPolygons(geometryToPolygons(legacyBoundary));
      if (!resolved) return;
      writeCachedBounds(tenantId, resolved);
      apply(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [tenantId, legacyBoundary]);

  const south = bounds?.[0][0] ?? null;
  const west = bounds?.[0][1] ?? null;
  const north = bounds?.[1][0] ?? null;
  const east = bounds?.[1][1] ?? null;

  // Keyed on the four numbers, NOT on `bounds` — re-reading the same cached
  // box must not produce a new object identity, or `FitBoundsToData` would
  // re-fit and discard wherever the user had panned to.
  return useMemo<FpaMapHome>(() => {
    if (south != null && west != null && north != null && east != null) {
      const box: LatLngBounds = [[south, west], [north, east]];
      return {
        center: centerOfBounds(box),
        zoom: zoomForBounds(box),
        fitBoundsTo: {
          polygons: [
            {
              positions: [
                [south, west],
                [south, east],
                [north, east],
                [north, west],
              ],
            },
          ],
        },
      };
    }

    const lat = currentTenant?.latitude;
    const lng = currentTenant?.longitude;
    if (lat != null && lng != null) {
      return { center: [Number(lat), Number(lng)], zoom: POINT_ZOOM, fitBoundsTo: false };
    }

    return { center: NATIONAL_CENTER, zoom: NATIONAL_ZOOM, fitBoundsTo: false };
  }, [south, west, north, east, currentTenant?.latitude, currentTenant?.longitude]);
}

export default useFpaMapHome;
